import { useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import ClaudeCodeLoginGate from '../../components/claude-code/ClaudeCodeLoginGate';
import {
  HELP_TEXT,
  ROLE_ASSET_KIND_HELP,
  ROLE_ASSET_SCOPE_HELP,
  ROLE_ASSET_STATUS_HELP,
} from '../../components/claude-code/agentTelemetryHelp';
import { HelpBadge, HelpLabel } from '../../components/claude-code/HelpTooltip';
import { useAgentRoleAssets } from '../../apps/apis/claudeCodeDashboard/hooks';
import type {
  AgentRoleAssetKind,
  AgentRoleAssetRow,
  AgentRoleAssetScope,
  AgentRoleAssetStatus,
} from '../../apps/apis/claudeCodeDashboard/types';
import { ROUTES } from '../../routes/routes';
import {
  formatDate,
  formatCost,
  formatCount,
  KIND_OPTIONS,
  kindLabel,
  metricHealthVariant,
  ROLE_OPTIONS,
  roleSummary,
  SCOPE_OPTIONS,
  scopeLabel,
  STATUS_OPTIONS,
  statusVariant,
} from './roleAssetUi';

const AgentRoleAssetsPage = () => (
  <ClaudeCodeLoginGate>
    {(session) => (
      <Container fluid className="py-4">
        <div className="d-flex align-items-end justify-content-between gap-2 mb-3 flex-wrap">
          <div>
            <Link to={ROUTES.agentTelemetry.overview} className="small text-muted">
              Back to Agent Telemetry
            </Link>
            <h2 className="mb-1">
              <HelpLabel description={HELP_TEXT.roleAssetLibrary}>Role Asset Library</HelpLabel>
            </h2>
            <div className="text-muted small">
              Signed in as <strong>{session.email}</strong>
              {session.isAdmin ? <span className="ms-2 badge bg-primary">admin</span> : null}
            </div>
          </div>
          <Link to={ROUTES.agentTelemetry.roleAssetsByRole} className="btn btn-outline-primary btn-sm">
            View by role
          </Link>
        </div>
        {session.isAdmin ? (
          <RoleAssetsBody />
        ) : (
          <Alert variant="warning">
            Role Asset Library is admin-only. Personal skill proposals remain available from the proposal page.
          </Alert>
        )}
      </Container>
    )}
  </ClaudeCodeLoginGate>
);

function RoleAssetsBody() {
  const [searchParams] = useSearchParams();
  const [kind, setKind] = useState<'' | AgentRoleAssetKind>('');
  const [scope, setScope] = useState<'' | AgentRoleAssetScope>('');
  const [status, setStatus] = useState<'' | AgentRoleAssetStatus>('');
  const [role, setRole] = useState(searchParams.get('role') ?? '');
  const [adapter, setAdapter] = useState('');
  const query = useAgentRoleAssets({ kind, scope, status, role, adapter, limit: 250 });

  const assets = query.data?.assets ?? [];
  const companyWideAssets = assets.filter((asset) => asset.scope === 'company_wide');
  const roleScopedAssets = assets.filter((asset) => asset.scope !== 'company_wide');

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex gap-2 flex-wrap align-items-end">
            <Form.Group style={{ minWidth: 170 }}>
              <Form.Label className="small text-muted">
                <HelpLabel description="Filter assets by draft, active, or retired governance state.">Status</HelpLabel>
              </Form.Label>
              <Form.Select
                value={status}
                onChange={(event) => setStatus(event.target.value as '' | AgentRoleAssetStatus)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ minWidth: 170 }}>
              <Form.Label className="small text-muted">
                <HelpLabel description={HELP_TEXT.assetKind}>Kind</HelpLabel>
              </Form.Label>
              <Form.Select value={kind} onChange={(event) => setKind(event.target.value as '' | AgentRoleAssetKind)}>
                {KIND_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ minWidth: 180 }}>
              <Form.Label className="small text-muted">
                <HelpLabel description={HELP_TEXT.assetScope}>Scope</HelpLabel>
              </Form.Label>
              <Form.Select value={scope} onChange={(event) => setScope(event.target.value as '' | AgentRoleAssetScope)}>
                {SCOPE_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ minWidth: 220 }}>
              <Form.Label className="small text-muted">
                <HelpLabel description={HELP_TEXT.roles}>Role</HelpLabel>
              </Form.Label>
              <Form.Select value={role} onChange={(event) => setRole(event.target.value)}>
                <option value="">All roles</option>
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ minWidth: 180 }}>
              <Form.Label className="small text-muted">
                <HelpLabel description="Runtime adapter where this asset is installed, such as Claude Code or Codex.">
                  Adapter
                </HelpLabel>
              </Form.Label>
              <Form.Control
                value={adapter}
                onChange={(event) => setAdapter(event.target.value)}
                placeholder="codex, claude"
              />
            </Form.Group>
            <Button variant="outline-secondary" onClick={() => void query.refetch()} disabled={query.isFetching}>
              Refresh
            </Button>
          </div>
          <div className="text-muted small mt-2">
            Adapter filtering is applied locally until the backend adds a first-class adapter query parameter.
          </div>
        </Card.Body>
      </Card>

      {query.isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : null}
      {query.error ? (
        <Alert variant="danger">Failed to load role assets: {(query.error as Error).message}</Alert>
      ) : null}
      {!query.isLoading && !query.error ? (
        <Row className="g-3">
          <Col xl={12}>
            <RoleAssetSection
              title="Company-wide assets"
              assets={companyWideAssets}
              emptyText="No company-wide assets match these filters."
            />
          </Col>
          <Col xl={12}>
            <RoleAssetSection
              title="Role-specific assets"
              assets={roleScopedAssets}
              emptyText="No role-specific assets match these filters."
            />
          </Col>
        </Row>
      ) : null}
    </>
  );
}

interface RoleAssetSectionProps {
  title: string;
  assets: AgentRoleAssetRow[];
  emptyText: string;
}

function RoleAssetSection({ title, assets, emptyText }: RoleAssetSectionProps) {
  const titleDescription = title === 'Company-wide assets' ? HELP_TEXT.companyWideAssets : HELP_TEXT.roleSpecificAssets;

  return (
    <Card>
      <Card.Header className="d-flex align-items-center justify-content-between gap-2">
        <div className="fw-semibold">
          <HelpLabel description={titleDescription}>{title}</HelpLabel>
        </div>
        <HelpBadge bg="secondary" description="Number of assets in this section after current filters.">
          {assets.length.toLocaleString()}
        </HelpBadge>
      </Card.Header>
      <Card.Body className="p-0">
        {assets.length ? (
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>
                    <HelpLabel description={HELP_TEXT.asset}>Asset</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.assetKind}>Kind</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.assetScope}>Scope</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.assetTargeting}>Targeting</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description="Current governance state for plugin distribution.">Status</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.assetVersion}>Version</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.adoption}>Adoption</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.usage}>Usage</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.failures}>Failures</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.costUsd}>Cost</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description="Last update timestamp for this asset record.">Updated</HelpLabel>
                  </th>
                </tr>
              </thead>
              <tbody>
                {assets.map((asset) => (
                  <RoleAssetRowItem key={asset.assetId} asset={asset} />
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="p-4 text-muted">{emptyText}</div>
        )}
      </Card.Body>
    </Card>
  );
}

function RoleAssetRowItem({ asset }: { asset: AgentRoleAssetRow }) {
  return (
    <tr>
      <td style={{ minWidth: 280 }}>
        <Link to={ROUTES.agentTelemetry.roleAssetDetail(asset.assetId)} className="fw-semibold">
          {asset.title}
        </Link>
        <div className="text-muted small">
          {asset.adapter} · {asset.assetId}
        </div>
      </td>
      <td>
        <HelpLabel description={ROLE_ASSET_KIND_HELP[asset.kind]}>{kindLabel(asset.kind)}</HelpLabel>
      </td>
      <td>
        <HelpLabel description={ROLE_ASSET_SCOPE_HELP[asset.scope]}>{scopeLabel(asset.scope)}</HelpLabel>
      </td>
      <td>{roleSummary(asset)}</td>
      <td>
        <HelpBadge bg={statusVariant(asset.status)} description={ROLE_ASSET_STATUS_HELP[asset.status]}>
          {asset.status}
        </HelpBadge>
      </td>
      <td>
        active {asset.activeVersion ?? '-'} / latest {asset.latestVersion}
      </td>
      <td>
        <div className="fw-semibold">
          {formatCount(asset.metrics.appliedEmployeeCount)} / {formatCount(asset.metrics.eligibleEmployeeCount)}
        </div>
        <div className="text-muted small">
          {formatCount(asset.metrics.activeEmployeeCount)} active · {formatCount(asset.metrics.appliedCount)} applies
        </div>
      </td>
      <td>
        <HelpBadge bg={metricHealthVariant(asset)} description={HELP_TEXT.usage}>
          {formatCount(asset.metrics.usageCount)}
        </HelpBadge>
        <div className="text-muted small">{formatCount(asset.metrics.impactedSessionCount)} sessions</div>
      </td>
      <td>
        <HelpBadge bg={asset.metrics.failedCount > 0 ? 'danger' : 'secondary'} description={HELP_TEXT.failures}>
          {formatCount(asset.metrics.failedCount)}
        </HelpBadge>
        {asset.metrics.skippedCount > 0 ? (
          <div className="text-muted small">{formatCount(asset.metrics.skippedCount)} skipped</div>
        ) : null}
      </td>
      <td>{formatCost(asset.metrics.impactedCostUsd)}</td>
      <td>{formatDate(asset.updatedAt)}</td>
    </tr>
  );
}

export default AgentRoleAssetsPage;
